import { Category, SupplierMapping } from '@synergy/models';
import { EditIcon } from '@synergy/theme';
import { IconButton, TableCell, TableRow } from '@mui/material';
import { Localized, PoolChip } from '@synergy/components';
import { makeStyles } from '@mui/styles';
import { padZeroLeft } from '@synergy/utils';
import { useEffect, useState } from 'react';

import { PaloDisclosureArrow } from '../../../../../common/components/palo/palo-disclosure-arrow';

const useStyles = makeStyles(() => ({
    icon: {
        fontSize: 16,
        color: '#21272E',
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
    },
    clickable: {
        cursor: 'pointer',
    },
    chip: {
        marginLeft: 0,
        marginRight: 8,
        backgroundColor: '#F2F2F2',
    },
    defaultChip: {
        backgroundColor: 'unset',
        border: '1px solid #F2F2F2',
        color: '#757575',
    },
    dash: {
        marginLeft: 8,
    },
}));

export interface SupplierCategoryMappingTableRowProps {
    categories: SupplierMapping<Category>[];
    category: SupplierMapping<Category>;
    filterIds?: number[];
    depth: number;
    onEdit: (item: SupplierMapping<Category>) => void;
}

export const SupplierCategoryMappingTableRow = (props: SupplierCategoryMappingTableRowProps) => {
    const classes = useStyles();
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setIsExpanded(!!props.filterIds);
    }, [props.filterIds]);

    const toggle = () => setIsExpanded(!isExpanded);

    const handleEdit = (supplierMapping: SupplierMapping<Category>) => {
        props.onEdit(supplierMapping);
    };

    const childCategories = props.categories.filter(category => category.parentId === props.category.id);
    const isExpandable = childCategories.length > 0;
    const indent = props.depth * 48;

    if (props.filterIds && !props.filterIds.includes(props.category.id)) {
        return null;
    }

    return (
        <>
            <TableRow>
                <TableCell
                    onClick={isExpandable ? toggle : undefined}
                    className={isExpandable ? classes.clickable : ''}
                >
                    <div className={classes.content}>
                        <div style={{ width: indent }} />
                        {isExpandable && <PaloDisclosureArrow open={isExpanded} />}
                        <div style={{ width: isExpandable ? 32 : 56 }} />
                        <Localized value={props.category.translations} /> ({padZeroLeft(props.category.id, 3)})
                    </div>
                </TableCell>
                <TableCell>
                    {props.category.defaultMapping.length > 0 ? (
                        props.category.defaultMapping.map(mapItem => (
                            <PoolChip key={mapItem} className={classes.defaultChip} label={mapItem} type={'tag'} />
                        ))
                    ) : (
                        <span className={classes.dash}>&ndash;</span>
                    )}
                </TableCell>
                <TableCell>
                    {props.category.supplierMapping.length > 0 ? (
                        props.category.supplierMapping.map(mapItem => (
                            <PoolChip className={classes.chip} label={mapItem} type={'tag'} />
                        ))
                    ) : (
                        <span className={classes.dash}>&ndash;</span>
                    )}
                </TableCell>
                <TableCell>
                    <IconButton onClick={() => handleEdit(props.category)}>
                        <EditIcon color="primary" />
                    </IconButton>
                </TableCell>
            </TableRow>
            {isExpandable &&
                isExpanded &&
                childCategories.map(childCategory => (
                    <SupplierCategoryMappingTableRow
                        key={childCategory.id}
                        categories={props.categories}
                        category={childCategory}
                        filterIds={props.filterIds}
                        depth={props.depth + 1}
                        onEdit={props.onEdit}
                    />
                ))}
        </>
    );
};
